import { sessionGet, sessionRemove, sessionSet } from './storage';

/**
 * Скин паспорта под сервис: `?skin=socpower`. Меняет палитру (переменные в index.css под
 * `html[data-skin]`), шапку, ссылки на документы и экран «Авторизация успешна» — без чужих сервисов.
 *
 * Живёт в sessionStorage: VK ID и Яндекс возвращают на голый origin, `?skin=` до возврата не доживает.
 * Свежее открытие другим сервисом (есть return_url/service/only, но нет skin) скин снимает.
 */
export type Skin = {
  id: 'socpower';
  /** Как сервис называет себя людям. */
  appName: string;
  /** Куда вернуть, если return_url потерялся (открыли паспорт напрямую или пришли кнопкой «Назад»). */
  homeUrl: string;
  /** Способы входа сервиса — если `?only=` в адресе уже нет (вернулись с id.vk.ru на голый origin). */
  only: string;
};

const SKINS: Record<Skin['id'], Skin> = {
  socpower: {
    id: 'socpower',
    appName: 'СОЦПАВЕР.РФ',
    homeUrl: 'https://xn--80aej7afjb5b.xn--p1ai',
    // Вход в кабинет — только почта (20.10.2026); страницу ВКонтакте привязывают уже в кабинете.
    only: 'email',
  },
};

const SKIN_KEY = 'ssmm-skin';
/**
 * Сайт сервиса из return_url. Отдельно от `ssmm-return-url`: тот redirectToReturnUrl стирает сразу после
 * редиректа, и вернувшийся «Назад» человек оставался без адреса — видел выбор чужих приложений.
 */
const ORIGIN_KEY = 'ssmm-skin-origin';

const isSkinId = (v: string | null): v is Skin['id'] => !!v && v in SKINS;

/** Вызывается до рендера: запоминает скин из адреса и вешает его на <html>. */
export function initSkin(): void {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('skin');
  if (fromUrl !== null) {
    if (isSkinId(fromUrl)) sessionSet(SKIN_KEY, fromUrl);
    else sessionRemove(SKIN_KEY);
  } else if (params.has('return_url') || params.has('service') || params.has('only')) {
    sessionRemove(SKIN_KEY);
  }
  if (fromUrl !== null) {
    sessionRemove(ORIGIN_KEY);
    try {
      const ret = params.get('return_url');
      if (ret) sessionSet(ORIGIN_KEY, new URL(ret).origin);
    } catch {
      /* битый return_url — останется домашний адрес */
    }
  }
  const skin = getSkin();
  if (skin) {
    document.documentElement.dataset.skin = skin.id;
    document.title = `Вход — ${skin.appName}`;
  }
}

export function getSkin(): Skin | null {
  const id = sessionGet(SKIN_KEY);
  return isSkinId(id) ? SKINS[id] : null;
}

/** Сайт сервиса: откуда пришли (стенд, другой домен), иначе домашний адрес скина. */
export function skinOrigin(skin: Skin): string {
  return sessionGet(ORIGIN_KEY) || skin.homeUrl;
}

/** Вход в сервис с готовым токеном — так же, как вернул бы паспорт по return_url. */
export function skinLoginUrl(skin: Skin, token: string): string {
  const url = new URL('/login', skinOrigin(skin));
  url.searchParams.set('sso_source', 'ssmm');
  url.searchParams.set('token', token);
  return url.toString();
}
